import { contexts, hooks } from 'lib_ui-primitives';
import { continuousScanBridge } from 'lib_ui-services';
import { createElement as rc, useCallback, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import ContinuousScanContextProvider from './actionElement/MarkRecord/ContinuousScanContextProvider';
import ContinuousScanScreen from './actionElement/MarkRecord/ContinuousScanScreen';

// ContinuousScanScreen resolves to native-only null on web/Node (see ContinuousScanScreen.js) -
// swappable so tests can substitute a stand-in without needing a real native module.
const _p = {
    ContinuousScanScreen
};
export const _private = _p;

/**
 * Separate page/route for Continuous Scan feature
 * Wraps the continuous scan experience with state management context
 * Shows as a full-screen experience with header still visible
 */
export default function ContinuousScanPage() {
    const history = useHistory();
    const router = hooks.useRouter();
    const sessionContext = useContext(contexts.SessionContext);

    const tenantId =
        sessionContext?.tenant?.['identity:tenant']?._id ??
        sessionContext?.tenant?._id ??
        sessionContext?.profile?.tenant?.[0]?.['identity:tenant']?._id ??
        sessionContext?.profile?.tenant?.[0]?._id;
    const userId = sessionContext?.profile?.userId ?? sessionContext?.profile?._id ?? sessionContext?.user?._id;

    // Read scan params from the query string, NOT router state - useNavigation.js blocks every
    // history.push and replays it through the rules engine's navigate_application_route
    // workflow, which only forwards path/search (goToLocation calls history.push(newLocation)
    // with no second argument), silently dropping any history.push(path, state) state.
    const scanParams = {
        title: router.query.title || 'Count',
        streamType: router.query.streamType || 'inventory',
        sessionId: router.query.sessionId || null,
        locationId: router.query.locationId || null,
        // Tenant-authored header for the identifier column on Screen D's grid ('Item ID' on a
        // Blockly-configured Mass Update grid, 'Asset ID' for Physical Inventory Count). Resolved
        // by whichever screen handed off (see getIdColumnLabel) because the hNode that carries it
        // does not survive this route change - only path/search do.
        idLabel: router.query.idLabel || 'Asset ID'
    };

    // Set once the session has been explicitly ended via handleClose/handleFinish, so the
    // unmount safety net below doesn't also try to cancel a session that already finished
    // normally. Left false covers every OTHER way this page can unmount - the Android hardware
    // back button, or any other forced navigation away from Screens B/C/D - none of which call
    // either callback. Without this, native's session is never told to end: hasActiveSession()
    // stays true indefinitely, and MarkRecord's startSession effect silently reuses the same
    // stale sessionId the next time the user returns to the Count screen and pulls the trigger,
    // no matter how much later or what else they navigate through in between.
    const sessionEndedRef = useRef(false);

    const handleClose = useCallback(() => {
        // Abort the native session so the MQTT connection is closed and RFID goes back to normal mode.
        // Routed through continuousScanBridge (rather than requiring 'react-native' directly) so this
        // shares the same web-safe module lookup and error logging as MarkRecord's own abort call.
        // Not gated on scanParams.sessionId being truthy - ContinuousScanModule.cancelSession (Java)
        // ignores whatever sessionId it's given and operates on its own internally-tracked
        // scanSession instead, so a query-string sessionId that's empty (e.g. from a race between
        // this navigation and startSession's async credential lookup - see MarkRecord/index.js's
        // openContinuousScanRoute) must not skip the native call and leave a real session dangling.
        sessionEndedRef.current = true;
        continuousScanBridge
            .cancelContinuousScan({ sessionId: String(scanParams.sessionId || ''), reason: 'user-abandoned' })
            .catch(err => {
                console.warn('[ContinuousScanPage] Error aborting session:', err);
            });
        history.goBack();
    }, [history, scanParams.sessionId]);

    const handleFinish = useCallback(() => {
        sessionEndedRef.current = true;
        history.goBack();
    }, [history]);

    // Unmount safety net - see sessionEndedRef above for why this is needed. Not gated on
    // scanParams.sessionId being truthy, same reasoning as handleClose above.
    useEffect(() => {
        return () => {
            if (!sessionEndedRef.current) {
                continuousScanBridge
                    .cancelContinuousScan({
                        sessionId: String(scanParams.sessionId || ''),
                        reason: 'continuous-scan-page-unmounted'
                    })
                    .catch(err => {
                        console.warn('[ContinuousScanPage] Error aborting session on unmount:', err);
                    });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return rc(
        ContinuousScanContextProvider,
        { onClose: handleClose, onFinish: handleFinish },
        rc(_p.ContinuousScanScreen, {
            title: scanParams.title,
            streamType: scanParams.streamType,
            isOpen: true,
            onClose: handleClose,
            onFinish: handleFinish,
            tenantId,
            userId,
            sessionId: scanParams.sessionId,
            locationId: scanParams.locationId,
            idLabel: scanParams.idLabel
        })
    );
}
